import { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Clock, ChevronLeft, ChevronRight, Receipt, Timer,
} from "lucide-react";
import { fmtEur2 } from "@/lib/format";
import { toast } from "sonner";

const DAYS = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];

function isoMonday(base = new Date()) {
  const d = new Date(base);
  d.setHours(0,0,0,0);
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}
function addDaysIso(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

export default function TimeReport() {
  const [weekStart, setWeekStart] = useState(isoMonday());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const [invOpen, setInvOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [hourlyRate, setHourlyRate] = useState(50);
  const [invNote, setInvNote] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async (ws) => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/time", { params: { week_start: ws } });
      setReport(data);
    } catch (e) { toast.error(errMsg(e, "Errore report tempo")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(weekStart); /* eslint-disable-next-line */ }, [weekStart]);

  const shift = (days) => setWeekStart(addDaysIso(weekStart, days));
  const goToday = () => setWeekStart(isoMonday());

  const byClient = useMemo(() => {
    if (!report) return {};
    const g = {};
    report.projects.forEach(p => {
      if (!p.client_id) return;
      (g[p.client_id] ||= { name: p.client_name, projects: [] }).projects.push(p);
    });
    return g;
  }, [report]);

  const eligibleProjects = report?.projects.filter(p => p.client_id) || [];
  const totalSelected = eligibleProjects
    .filter(p => selectedIds.includes(p.project_id))
    .reduce((s,p) => s + p.hours, 0);
  const selectedClient = new Set(
    eligibleProjects.filter(p => selectedIds.includes(p.project_id)).map(p => p.client_id)
  );

  const toggleProject = (pid) => {
    setSelectedIds((prev) => prev.includes(pid) ? prev.filter(x=>x!==pid) : [...prev, pid]);
  };

  const openInvDialog = (client_id) => {
    const ids = eligibleProjects.filter(p=>p.client_id===client_id).map(p=>p.project_id);
    setSelectedIds(ids);
    setHourlyRate(50);
    setInvNote("");
    setInvOpen(true);
  };

  const createInvoice = async () => {
    if (selectedIds.length === 0) return toast.error("Seleziona almeno un progetto");
    if (selectedClient.size !== 1) return toast.error("Progetti di clienti diversi — seleziona quelli di un solo cliente");
    setCreating(true);
    try {
      const { data } = await api.post("/reports/time/invoice", {
        project_ids: selectedIds,
        hourly_rate: parseFloat(hourlyRate) || 0,
        week_start: weekStart,
        note: invNote || null,
      });
      toast.success(`Fattura ${data.invoice.number} creata: ${fmtEur2(data.amount)}`);
      setInvOpen(false);
      load(weekStart);
    } catch (e) { toast.error(errMsg(e, "Errore fattura")); }
    finally { setCreating(false); }
  };

  return (
    <div className="zen-card p-6" data-testid="time-report">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <span className="zen-label">Tempo lavorato</span>
          <h2 className="font-heading text-xl mt-1">Report settimanale</h2>
          {report && (
            <div className="text-sm text-zen-ink2 mt-1">
              {fmtDate(report.week_start)} → {fmtDate(report.week_end)} · <b>{report.total_hours.toFixed(2)}h</b> totali
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button data-testid="tr-prev" variant="outline" size="sm" onClick={()=>shift(-7)} className="rounded-full h-9 w-9 p-0">
            <ChevronLeft size={16}/>
          </Button>
          <Button data-testid="tr-today" variant="outline" size="sm" onClick={goToday} className="rounded-full">Oggi</Button>
          <Button data-testid="tr-next" variant="outline" size="sm" onClick={()=>shift(7)} className="rounded-full h-9 w-9 p-0">
            <ChevronRight size={16}/>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-zen-ink2 text-sm py-6 text-center">Caricamento…</div>
      ) : !report || report.projects.length === 0 ? (
        <div className="text-center py-10">
          <Timer size={36} strokeWidth={1.3} className="mx-auto text-zen-primary/40 mb-3"/>
          <div className="text-zen-ink2 text-sm">
            Nessuna ora registrata in questa settimana. Avvia il cronometro su un&apos;attività!
          </div>
        </div>
      ) : (
        <>
          {/* Day summary */}
          <div className="grid grid-cols-7 gap-2 mb-6">
            {report.days.map((h, i) => {
              const maxH = Math.max(...report.days, 1);
              const pct = (h / maxH) * 100;
              return (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <div className="w-full h-20 bg-zen-bg rounded-lg flex items-end overflow-hidden">
                    <div
                      className="w-full bg-zen-primary/60 transition-all"
                      style={{ height: `${pct}%` }}
                      title={`${h.toFixed(2)}h`}
                    />
                  </div>
                  <div className="text-xs text-zen-ink2">{DAYS[i]}</div>
                  <div className="text-xs font-mono font-medium">{h > 0 ? `${h.toFixed(1)}h` : "—"}</div>
                </div>
              );
            })}
          </div>

          {/* Projects table grouped by client */}
          <div className="space-y-4">
            {Object.entries(byClient).map(([cid, group]) => (
              <div key={cid} className="border border-zen-border rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-zen-bg/60">
                  <div className="font-medium text-zen-ink">{group.name}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-zen-ink2">
                      {group.projects.reduce((s,p)=>s+p.hours,0).toFixed(2)}h totali
                    </span>
                    <Button
                      data-testid={`tr-invoice-${cid}`}
                      size="sm"
                      onClick={()=>openInvDialog(cid)}
                      className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
                    >
                      <Receipt size={13} className="mr-1"/> Crea fattura oraria
                    </Button>
                  </div>
                </div>
                <div className="divide-y divide-zen-border">
                  {group.projects.map(p => (
                    <div key={p.project_id} className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-zen-ink">{p.project_name}</div>
                        <div className="text-xs text-zen-ink2 mt-0.5">
                          {p.task_count} attività · {p.days.map((d, i) => d > 0 ? `${DAYS[i]} ${d.toFixed(1)}h` : null).filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <div className="font-mono font-medium text-zen-primary shrink-0">{p.hours.toFixed(2)}h</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {report.projects.filter(p=>!p.client_id).length > 0 && (
              <div className="border border-zen-border rounded-xl px-4 py-3 bg-zen-bg/30 text-sm text-zen-ink2">
                <b>Senza cliente:</b>{" "}
                {report.projects.filter(p=>!p.client_id).map(p=>`${p.project_name} (${p.hours.toFixed(1)}h)`).join(", ")}
                <div className="text-xs mt-1">Assegna un cliente al progetto per fatturare.</div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Create hourly invoice dialog */}
      <Dialog open={invOpen} onOpenChange={setInvOpen}>
        <DialogContent className="max-w-lg" data-testid="tr-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Crea fattura oraria</DialogTitle>
            <DialogDescription>
              Fattura le ore effettivamente registrate in questa settimana con la tua tariffa oraria.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Progetti inclusi</Label>
              <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
                {eligibleProjects.map(p => (
                  <label
                    key={p.project_id}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-zen-bg/60 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      data-testid={`tr-sel-${p.project_id}`}
                      checked={selectedIds.includes(p.project_id)}
                      onChange={()=>toggleProject(p.project_id)}
                      className="accent-zen-primary"
                    />
                    <span className="flex-1 text-sm">{p.project_name}</span>
                    <span className="text-xs text-zen-ink2 font-mono">{p.hours.toFixed(2)}h</span>
                    <span className="text-xs text-zen-ink2">· {p.client_name}</span>
                  </label>
                ))}
              </div>
              {selectedClient.size > 1 && (
                <p className="text-xs text-zen-alert mt-1">Progetti di più clienti — seleziona quelli di un solo cliente per una fattura.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tariffa oraria (€)</Label>
                <Input
                  data-testid="tr-rate"
                  type="number" step="1" min="0"
                  value={hourlyRate}
                  onChange={(e)=>setHourlyRate(e.target.value)}
                />
              </div>
              <div>
                <Label>Totale stimato</Label>
                <div className="mt-1 h-10 flex items-center px-3 rounded-xl bg-zen-bg font-heading text-lg text-zen-primary" data-testid="tr-total">
                  {fmtEur2((parseFloat(hourlyRate) || 0) * totalSelected)}
                </div>
                <div className="text-xs text-zen-ink2 mt-1">{totalSelected.toFixed(2)}h × €{hourlyRate || 0}</div>
              </div>
            </div>
            <div>
              <Label>Nota aggiuntiva (opzionale)</Label>
              <Textarea
                data-testid="tr-note"
                rows={2}
                value={invNote}
                onChange={(e)=>setInvNote(e.target.value)}
                placeholder="Es. Include revisioni concordate a voce"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setInvOpen(false)}>Annulla</Button>
            <Button
              data-testid="tr-create-invoice"
              onClick={createInvoice}
              disabled={creating || totalSelected <= 0}
              className="bg-zen-primary hover:bg-zen-primaryHover"
            >
              {creating ? "Creo…" : "Crea fattura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
